// src/features/auth/AccountLinkCard.jsx
// アカウントの「もう一方の入口」を足す案内。
//   - LINEログインで入った人 … 電話番号がまだ未認証。金銭が絡む操作（まいウォレット残高・
//     ポイント利用）の前に SMS 認証を求めるので、ここで先に済ませられるようにする。
//   - 電話番号SMSで入った人 … LINE連携を勧める（次回からコード入力なし／受取・入荷の連絡がLINEに届く）。
//
// ⚠LINE連携は「LINEアプリ内ブラウザには Firebase のセッションが無い」ため、ワンタイムチケットを
//   URL で運ぶ（lib/lineAuthApi の startLineLink / Core の lib/lineLinkTicket.js 参照）。
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RecaptchaVerifier,
  linkWithPhoneNumber,
  reload,
} from "firebase/auth";
import { MessageCircle, ShieldCheck, Check, X, AlertTriangle } from "lucide-react";

import Loader from "../../components/Loader";
import { auth } from "../../firebase";
import { TENANT_ID, getTenantPathPrefix } from "../../tenant";
import { buildLineLoginUrl, isLineLoginEnabled } from "../../lib/liff";
import { startLineLink, saveCustomerContact } from "../../lib/lineAuthApi";

const RECAPTCHA_ID = "recaptcha-link-phone";

function toE164JP(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.startsWith("0") ? `+81${digits.slice(1)}` : `+81${digits}`;
}

export default function AccountLinkCard({ profile }) {
  const lineEnabled = useMemo(() => isLineLoginEnabled(), []);

  const providers = useMemo(() => {
    const v = profile?.providers;
    return Array.isArray(v) ? v : [];
  }, [profile?.providers]);

  // 認証済みかどうかの正は Firebase Auth の電話番号（顧客ドキュメントのフラグは表示用の写し）
  const [authPhone, setAuthPhone] = useState(
    () => String(auth.currentUser?.phoneNumber || "").trim()
  );
  const phoneVerified = !!authPhone;
  const lineLinked = providers.includes("line") || !!profile?.line_user_id;

  // 入力された電話番号で作られた別アカウントが既にある（サーバーが Auth を照合して立てる印）。
  // 以前 SMS で登録していた人が「LINEと連携」ではなく「LINEでログイン」から入ると起きる。
  // 自動統合はできないので、購入済みのチケット等がある旧アカウントへ戻す案内を出す。
  const phoneConflict = profile?.phone_conflict === true && !phoneVerified;

  const [step, setStep] = useState("idle"); // idle | PHONE | OTP | done
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const verifierRef = useRef(null);
  const confirmationRef = useRef(null);

  useEffect(() => {
    return () => {
      try {
        verifierRef.current?.clear();
      } catch (_e) {
        /* noop */
      }
      verifierRef.current = null;
    };
  }, []);

  const handleStartLineLink = async () => {
    setError("");
    setBusy(true);
    try {
      const { ticket } = await startLineLink({ tenantId: TENANT_ID });
      const url = buildLineLoginUrl({
        link: ticket,
        t: TENANT_ID,
        r: `${getTenantPathPrefix()}/`,
      });
      if (!url) throw new Error("LINE連携は現在ご利用いただけません。");
      window.location.href = url;
    } catch (e) {
      console.error("[AccountLinkCard] startLineLink failed:", e);
      setError(e?.message || "LINE連携を開始できませんでした。");
      setBusy(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (busy) return;

    setError("");
    setBusy(true);

    try {
      if (!verifierRef.current) {
        verifierRef.current = new RecaptchaVerifier(auth, RECAPTCHA_ID, {
          size: "invisible",
        });
        await verifierRef.current.render();
      }

      const e164 = toE164JP(phone);
      confirmationRef.current = await linkWithPhoneNumber(
        auth.currentUser,
        e164,
        verifierRef.current
      );

      setStep("OTP");
    } catch (err) {
      console.error("[AccountLinkCard] linkWithPhoneNumber failed:", err);

      if (err?.code === "auth/credential-already-in-use") {
        // その番号で作られた古いアカウントが既にある。勝手に統合はできない
        // （購入履歴・残回数の持ち主を機械的に決められない）ので、人の操作に回す。
        setError(
          "この電話番号は、以前に作られた別のご利用アカウントで使われています。" +
            "一度ログアウトして、その電話番号でログインしたうえで、そちらからLINE連携をしてください。"
        );
      } else if (err?.code === "auth/too-many-requests") {
        setError("認証リクエストが集中しています。少し時間をおいてお試しください。");
      } else {
        setError(`SMS送信に失敗しました（${err?.code || "unknown"}）`);
      }

      try {
        verifierRef.current?.clear();
      } catch (_e) {
        /* noop */
      }
      verifierRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    if (busy) return;

    setError("");
    setBusy(true);

    try {
      await confirmationRef.current.confirm(otp);
      await reload(auth.currentUser);

      // 認証済みフラグはサーバーが Auth を読んで貼る
      await saveCustomerContact({ tenantId: TENANT_ID });

      setAuthPhone(String(auth.currentUser?.phoneNumber || "").trim());
      setNotice("電話番号の認証が完了しました。");
      setStep("done");
    } catch (err) {
      console.error("[AccountLinkCard] confirm failed:", err);
      setError("認証コードが正しくありません。もう一度お試しください。");
    } finally {
      setBusy(false);
    }
  };

  // 両方そろっている人には何も出さない
  if (phoneVerified && (lineLinked || !lineEnabled) && !phoneConflict) return null;

  return (
    <div className="bg-white rounded-3xl p-4 border border-gray-200 space-y-3">
      <div id={RECAPTCHA_ID} />

      {notice && (
        <div className="flex items-start gap-2 bg-gray-50 text-gray-900 rounded-3xl px-4 py-3 text-sm font-bold">
          <Check size={16} className="mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 text-red-700 rounded-3xl px-4 py-3 text-sm font-bold">
          <X size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {phoneConflict && (
        <div className="flex items-start gap-2 bg-amber-50 text-amber-800 rounded-3xl px-4 py-3 text-sm font-bold">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="leading-relaxed">
            この電話番号では、以前からご利用のアカウントがあるようです。
            お買い上げいただいたチケットやプランはそちらに入っています。
            一度ログアウトして、<span className="underline">電話番号でログイン</span>し、
            そのアカウントから「LINEと連携する」をお願いします。
          </span>
        </div>
      )}

      {/* LINE未連携 */}
      {lineEnabled && !lineLinked && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-[#06C755]" />
            <p className="text-sm font-black text-gray-800">LINEと連携する</p>
          </div>
          <p className="text-xs font-bold text-gray-500 leading-relaxed">
            次回からコードの入力なしで入れます。受け取りや入荷のご連絡もLINEに届きます。
          </p>
          <button
            type="button"
            onClick={handleStartLineLink}
            disabled={busy}
            className="w-full text-white font-black py-4 rounded-3xl inline-flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: "#06C755" }}
          >
            {busy ? <Loader /> : "LINEと連携する"}
          </button>
        </div>
      )}

      {/* 電話番号が未認証（LINEで入った人） */}
      {!phoneVerified && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-gray-500" />
            <p className="text-sm font-black text-gray-800">電話番号を認証する</p>
          </div>
          <p className="text-xs font-bold text-gray-500 leading-relaxed">
            まいウォレットの残高やポイントのご利用には、SMSでの電話番号認証が必要です。
          </p>

          {step === "idle" && (
            <button
              type="button"
              onClick={() => {
                setError("");
                setPhone(String(profile?.phone || ""));
                setStep("PHONE");
              }}
              className="w-full bg-white border-2 border-gray-200 text-gray-700 font-black py-4 rounded-3xl"
            >
              SMSで認証する
            </button>
          )}

          {step === "PHONE" && (
            <form onSubmit={handleSendCode} className="space-y-2">
              <input
                type="tel"
                inputMode="tel"
                className="w-full p-3 rounded-xl font-bold text-gray-700 outline-none bg-white border-2 border-gray-200 hover:border-gray-300 focus:border-gray-900"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="090-1234-5678"
                autoComplete="tel"
              />
              <button
                type="submit"
                disabled={busy || String(phone).replace(/\D/g, "").length !== 11}
                className="w-full bg-gray-900 text-white font-black py-4 rounded-3xl disabled:opacity-50"
              >
                {busy ? <Loader /> : "認証コードを送る"}
              </button>
            </form>
          )}

          {step === "OTP" && (
            <form onSubmit={handleConfirmCode} className="space-y-2">
              <input
                type="text"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="w-full p-3 rounded-xl font-bold text-gray-700 outline-none bg-gray-50 border-2 border-gray-200 text-center tracking-widest text-xl"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
              />
              <button
                type="submit"
                disabled={busy || otp.length < 6}
                className="w-full bg-gray-900 text-white font-black py-4 rounded-3xl disabled:opacity-50"
              >
                {busy ? <Loader /> : "認証する"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
